import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { API_BASE_URL } from '../../config/api'

interface Subscriber {
  id: number
  email: string
  name: string | null
  state: string | null
  created_at: string
  status?: number | string
}

const SubscribeManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  // Fetch all subscribers
  const fetchSubscribers = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}newsletter_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        setSubscribers(result.data)
      } else {
        setError(result.message || 'Failed to fetch subscribers')
        setSubscribers([])
      }
    } catch (err) {
      setError('Failed to fetch subscribers. Please try again.')
      console.error('Error fetching subscribers:', err)
      setSubscribers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscribers()
  }, [])

  // Toggle subscriber status (subscribe/unsubscribe)
  const handleToggleStatus = async (id: number, currentStatus: number | string) => {
    const nextStatus = Number(currentStatus ?? 1) === 1 ? 0 : 1
    setUpdatingId(id)
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}newsletter_update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, status: nextStatus })
      })

      const result = await response.json()

      if (result.status) {
        // Update local state
        setSubscribers(prev =>
          prev.map(sub => (sub.id === id ? { ...sub, status: nextStatus } : sub))
        )
      } else {
        setError(result.message || 'Failed to update subscriber status')
      }
    } catch (err) {
      setError('Failed to update status. Please try again.')
      console.error('Error updating status:', err)
    } finally {
      setUpdatingId(null)
    }
  }

  // Filter subscribers based on search term (name, email, state)
  const filteredSubscribers = subscribers.filter(subscriber => {
    const term = searchTerm.toLowerCase()
    return (
      subscriber.email.toLowerCase().includes(term) ||
      (subscriber.name && subscriber.name.toLowerCase().includes(term)) ||
      (subscriber.state && subscriber.state.toLowerCase().includes(term))
    )
  })

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  // Export to CSV/Excel (UTF-8 BOM is added so Excel opens it with proper formatting)
  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'State', 'Date of Joining', 'Status']
    const rows = subscribers.map(sub => [
      sub.id,
      sub.name || 'N/A',
      sub.email,
      sub.state || 'N/A',
      sub.created_at || 'N/A',
      Number(sub.status ?? 1) === 1 ? 'Subscribed' : 'Unsubscribed'
    ])
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `subscribed-users-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-white">Subscribe User Management</h1>
                <p className="text-gray-400 mt-1 text-sm">Manage user newsletter subscriptions, toggle active states, and export data.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportToCSV}
                  disabled={subscribers.length === 0}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2 shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Export Excel</span>
                </button>
                <button
                  onClick={fetchSubscribers}
                  className="bg-primary-light hover:bg-primary-dark text-white px-5 py-2.5 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2 shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center space-x-2 text-red-200">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Search Input */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 rounded-lg bg-gray-800 text-white placeholder-gray-400 border-2 border-gray-700 focus:outline-none focus:border-primary-light transition-colors"
                />
                <svg
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-700/50 text-gray-300 uppercase text-xs font-bold border-b border-gray-700">
                      <th className="py-4 px-6">ID</th>
                      <th className="py-4 px-6">Name</th>
                      <th className="py-4 px-6">Email Address</th>
                      <th className="py-4 px-6">State (USA)</th>
                      <th className="py-4 px-6">Date of Joining</th>
                      <th className="py-4 px-6 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700 text-white">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-gray-400">
                          <div className="flex justify-center items-center space-x-2">
                            <svg className="animate-spin h-6 w-6 text-primary-light" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading subscribers...</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredSubscribers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-gray-400">
                          No subscribers found.
                        </td>
                      </tr>
                    ) : (
                      filteredSubscribers.map((subscriber) => {
                        const isSubscribed = Number(subscriber.status ?? 1) === 1
                        const isToggling = updatingId === subscriber.id

                        return (
                          <tr key={subscriber.id} className="hover:bg-gray-750 transition-colors">
                            <td className="py-4 px-6 text-gray-450 font-medium">{subscriber.id}</td>
                            <td className="py-4 px-6 font-semibold">{subscriber.name || <span className="text-gray-500 italic text-sm">N/A</span>}</td>
                            <td className="py-4 px-6 text-gray-300 font-mono">{subscriber.email}</td>
                            <td className="py-4 px-6">{subscriber.state || <span className="text-gray-500 italic text-sm">N/A</span>}</td>
                            <td className="py-4 px-6 text-gray-400 text-sm">{formatDate(subscriber.created_at)}</td>
                            <td className="py-4 px-6">
                              <div className="flex items-center justify-center">
                                <button
                                  onClick={() => handleToggleStatus(subscriber.id, subscriber.status ?? 1)}
                                  disabled={isToggling}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-2 focus:ring-offset-gray-800 ${
                                    isSubscribed ? 'bg-green-500' : 'bg-gray-600'
                                  } ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                  <span className="sr-only">Toggle Subscribe Status</span>
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                                      isSubscribed ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <span className={`ml-3 text-sm font-semibold select-none min-w-[90px] ${
                                  isSubscribed ? 'text-green-400' : 'text-gray-400'
                                }`}>
                                  {isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  )
}

export default SubscribeManagement
